/**
 * Los tipos de la orden de pago, y el vocabulario de su estado.
 *
 * ⚠ **Los montos son cadenas.** Viajan así desde el backend porque un `number` de
 * JavaScript pierde céntimos en importes grandes; se formatean, no se convierten.
 */

export type EstadoOrden =
  | "BORRADOR"
  | "EN_APROBACION"
  | "APROBADA"
  | "EJECUTADA"
  | "RECHAZADA"
  | "ANULADA"

export type Linea = {
  id: number
  descripcion: string
  cantidad: string
  precio_unitario: string
  monto: string
  gasto_id: number | null
  /**
   * Con artículo, la línea es una compra de bienes y al pagar entra al depósito con
   * esta cantidad y este precio; sin él, es un servicio y no mueve nada. Va en la
   * LÍNEA y no en la orden porque una misma compra trae las dos cosas: la reparación
   * de la tubería y el material salieron del banco en la misma transferencia.
   */
  articulo_id: number | null
  /**
   * Material que se compra para usarlo ya —los tornillos de una reparación— y no
   * pasa por el depósito. Siempre sin artículo.
   */
  uso_inmediato?: boolean
  /** Lo que se le paga a una persona de la casa. Lo pone la nómina. */
  es_sueldo?: boolean
  /** El nombre del artículo, para no tener que resolverlo al pintar la línea. */
  articulo?: string | null
  /**
   * Consumible o bien; `null` si la línea no lleva artículo.
   *
   * ⚠ **Con `articulo_id` sólo se sabe que compra algo, no QUÉ.** Un tóner y una
   * fotocopiadora se administran distinto —uno se repone, la otra se deprecia—, y en
   * el desglose de la factura tienen que distinguirse.
   */
  articulo_clase?: "CONSUMIBLE" | "BIEN" | null
  /** Su categoría de gasto propia; `null` es la de la orden. */
  categoria_id?: number | null
  /** Su nombre, para pintarla sin resolverla aparte. */
  categoria_nombre?: string | null
  /**
   * Si este renglón paga IVA. Va en la línea porque una misma factura trae lo gravado
   * y lo exento —el papel y los libros—, y marcar sólo la orden entera le aplicaría el
   * 16 % a lo exento: el total saldría inflado con un impuesto que nadie cobró.
   */
  exento_iva: boolean
}

export type Documento = {
  id: number
  tipo: "FACTURA" | "COMPROBANTE" | "OTRO"
  /** Su número dentro de la orden. Lo asigna el servidor y no se reusa al borrar. */
  correlativo: number
  /** `OP-2026-0036-01.png`: con el que se enseña y se descarga. */
  nombre: string
  /** Con el que llegó: lo único que dice de dónde salió el archivo. */
  nombre_original: string
  mime: string
  bytes: number
  creado_en: string
}

/** Qué se decidió al pagar otra cifra que la pendiente. */
export type TratamientoDiferencia = "CERRAR" | "SALDO_PROVEEDOR" | "SALDO_EMPRESA"

/** Saldo a favor del proveedor aplicado a pagar la orden. No movió el banco. */
export type AplicacionSaldo = {
  id: number
  monto: string
  fecha_valor: string
  aplicada_por: number | null
  creada_en: string
  anulada: boolean
  motivo_anulacion: string | null
}

export type Ejecucion = {
  id: number
  cuenta_id: number
  cuenta: string | null
  metodo_pago_id: number | null
  metodo_pago: string | null
  monto_debitado: string
  /** Lo que vale el pago en la moneda de la ORDEN. */
  monto_equivalente: string
  /** Contra lo pendiente al pagar: negativo, de menos; positivo, de más. */
  monto_diferencia: string
  tratamiento_diferencia: TratamientoDiferencia | null
  tasa_aplicada: string | null
  comision: string
  monto_igtf: string
  referencia: string
  fecha_valor: string
  /** El comprobante, si el pago produjo uno. Ver `motivo_sin_comprobante`. */
  documento_id: number | null
  /**
   * Por qué este pago no tiene comprobante. Hay movimientos que no producen uno —un
   * cruce de fondos entre cuentas propias—, y entonces el respaldo es este texto.
   */
  motivo_sin_comprobante?: string | null
  ejecutada_por: number | null
  creada_en: string
  anulada: boolean
  motivo_anulacion: string | null
}

export type Orden = {
  id: number
  codigo: string
  estado: EstadoOrden
  proveedor_id: number
  proveedor: string | null
  expediente_id: number | null
  categoria_id: number | null
  categoria: string | null
  /** La partida del plan de cuentas de su categoría (28/10). */
  cuenta_contable?: string | null
  gasto_id: number | null
  concepto: string
  divisa_id: number
  divisa: string | null
  monto_base: string
  /** La parte de la base que no paga IVA: la suma de los renglones exentos. */
  monto_exento: string
  /** `monto_base − monto_exento`. Lo calcula el servidor: aquí restar cadenas obligaría
   * a convertirlas a `number`, que es justo lo que pierde céntimos. */
  monto_gravado: string
  aplica_iva: boolean
  tasa_iva: string
  monto_iva: string
  aplica_ret_iva: boolean
  tasa_ret_iva: string
  monto_ret_iva: string
  aplica_islr: boolean
  alicuota_islr: string
  sustraendo_islr: string
  monto_islr: string
  /** Impuestos que NO son IVA venezolano (sales tax de EE. UU.): suman al total. */
  monto_otros_impuestos: string
  concepto_otros_impuestos: string | null
  monto_total: string
  monto_neto: string
  numero_documento: string | null
  numero_control: string | null
  /** Una solicitud de fondos sin factura: no entra al libro de compras. */
  sin_documento_fiscal?: boolean
  fecha_emision: string | null
  fecha_vencimiento: string | null
  fecha_requerida: string | null
  notas: string | null
  creada_por: number | null
  aprobada_por: number | null
  aprobada_en: string | null
  motivo_rechazo: string | null
  motivo_anulacion: string | null
  creada_en: string
  lineas: Linea[]
  documentos: Documento[]
  /** El historial de pagos: vivo y revertidos. El vivo es el que no esta `anulada`. */
  ejecuciones: Ejecucion[]
  aplicaciones_saldo: AplicacionSaldo[]
  /** Lo cubierto por pagos vivos. Lo calcula el servidor desde las ejecuciones. */
  monto_pagado: string
  /** Lo que falta. Cero en una orden cerrada aunque se pagara de menos. */
  monto_pendiente: string
}

export const ESTADOS: readonly { valor: EstadoOrden; etiqueta: string }[] = [
  { valor: "BORRADOR", etiqueta: "Borrador" },
  { valor: "EN_APROBACION", etiqueta: "En aprobación" },
  { valor: "APROBADA", etiqueta: "Aprobada" },
  { valor: "EJECUTADA", etiqueta: "Ejecutada" },
  { valor: "ANULADA", etiqueta: "Anulada" },
] as const

/** El tono NO dice «bien» o «mal»: dice en qué punto del recorrido está. */
export const TONO_ESTADO: Record<
  EstadoOrden,
  "neutro" | "info" | "aviso" | "exito" | "peligro" | "primario"
> = {
  BORRADOR: "neutro",
  EN_APROBACION: "aviso",
  APROBADA: "info",
  EJECUTADA: "exito",
  RECHAZADA: "peligro",
  ANULADA: "neutro",
}
